import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Link } from 'react-router-dom';
import Logo from './Logo';

const pages = [
  { title: 'Home', path: '/' },
  { title: 'About', path: '/about' },
  { title: 'Products', path: '/products' },
  { title: 'Services', path: '/services' },
  { title: 'Training', path: '/training' },
  { title: 'Contact', path: '/contact' },
  { title: 'FAQ', path: '/faq' },
];

const menuTextStyles = {
  fontWeight: 600,
  fontSize: '1.125rem',
};

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [productsAnchorEl, setProductsAnchorEl] = useState(null);
  const [mobileProductsAnchorEl, setMobileProductsAnchorEl] = useState(null);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [mobileServicesAnchorEl, setMobileServicesAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const productsSubmenu = [
    { title: 'Safety Database', path: '/products/safety-database' },
    { title: 'Learning Management System', path: '/products/learning-management-system' },
  ];

  const aboutSubmenu = [
    { title: 'About Us', path: '/about' },
    { title: 'Leadership Team', path: '/leadership' },
  ];

  const [aboutAnchorEl, setAboutAnchorEl] = useState(null);
  const [mobileAboutAnchorEl, setMobileAboutAnchorEl] = useState(null);

  const servicesSubmenu = [
    { title: 'Pharmacovigilance System Setup', path: '/services/pharmacovigilance-system-setup' },
    { title: 'Provision of Safety Database', path: '/services/provision-of-safety-database' },
    { title: 'Audit and Inspection Support', path: '/services/audit-and-inspection-support' },
    { title: 'QPPV Services', path: '/services/qppv-services' },
    { title: 'Pharmacovigilance System Master File', path: '/services/pharmacovigilance-system-master-file' },
    { title: 'Safety Data Exchange Agreements (SDEA) Management', path: '/services/sdea-management' },
    { title: 'Safety Data Migration Assistance', path: '/services/safety-data-migration-assistance' },
    { title: 'Literature Translation & Full Text Articles', path: '/services/literature-translation-and-full-text' },
  ];

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleProductsMenuOpen = (event) => {
    if (isMobile) {
      setMobileProductsAnchorEl(event.currentTarget);
    } else {
      setProductsAnchorEl(event.currentTarget);
    }
  };

  const handleAboutMenuOpen = (event) => {
    if (isMobile) {
      setMobileAboutAnchorEl(event.currentTarget);
    } else {
      setAboutAnchorEl(event.currentTarget);
    }
  };

  const handleAboutMenuClose = () => {
    setAboutAnchorEl(null);
    setMobileAboutAnchorEl(null);
  };

  const handleProductsMenuClose = () => {
    setProductsAnchorEl(null);
    setMobileProductsAnchorEl(null);
  };

  const handleServicesMenuOpen = (event) => {
    if (isMobile) {
      setMobileServicesAnchorEl(event.currentTarget);
    } else {
      setServicesAnchorEl(event.currentTarget);
    }
  };

  const handleServicesMenuClose = () => {
    setServicesAnchorEl(null);
    setMobileServicesAnchorEl(null);
  };

  return (
    <AppBar 
      position="sticky" 
      color="default" 
      elevation={1}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Desktop Logo - Top Left */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 3, alignItems: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Logo />
            </Link>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.title}
                  onClick={
                    page.title === 'Products'
                      ? handleProductsMenuOpen
                      : page.title === 'Services'
                      ? handleServicesMenuOpen
                      : page.title === 'About'
                      ? handleAboutMenuOpen
                      : handleCloseNavMenu
                  }
                >
                  {page.title === 'Products' || page.title === 'Services' || page.title === 'About' ? (
                    <Typography textAlign="center" sx={menuTextStyles}>
                      {page.title} <ArrowDropDownIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />
                    </Typography>
                  ) : (
                    <Link
                      to={page.path}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Typography textAlign="center" sx={menuTextStyles}>
                        {page.title}
                      </Typography>
                    </Link>
                  )}
                </MenuItem>
              ))}
              <Menu
                anchorEl={mobileAboutAnchorEl}
                open={Boolean(mobileAboutAnchorEl)}
                onClose={handleAboutMenuClose}
              >
                {aboutSubmenu.map((item) => (
                  <MenuItem key={item.title} component={Link} to={item.path} onClick={handleAboutMenuClose}>
                    <Typography sx={menuTextStyles}>{item.title}</Typography>
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={mobileProductsAnchorEl}
                open={Boolean(mobileProductsAnchorEl)}
                onClose={handleProductsMenuClose}
              >
                {productsSubmenu.map((item) => (
                  <MenuItem key={item.title} component={Link} to={item.path} onClick={handleProductsMenuClose}>
                    <Typography sx={menuTextStyles}>{item.title}</Typography>
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={mobileServicesAnchorEl}
                open={Boolean(mobileServicesAnchorEl)}
                onClose={handleServicesMenuClose}
              >
                {servicesSubmenu.map((item) => (
                  <MenuItem key={item.title} component={Link} to={item.path} onClick={handleServicesMenuClose}>
                    <Typography sx={menuTextStyles}>{item.title}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Menu>
          </Box>

          {/* Mobile Logo - Top Left */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1, alignItems: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Logo />
            </Link>
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            {pages.map((page) => {
              if (page.title === 'About') {
                return (
                  <Box
                    key={page.title}
                    onMouseEnter={(e) => !isMobile && handleAboutMenuOpen(e)}
                    onMouseLeave={handleAboutMenuClose}
                  >
                    <Button
                      onClick={handleAboutMenuOpen}
                      sx={{
                        my: 2,
                        color: '#3c005a',
                        display: 'block',
                        fontWeight: menuTextStyles.fontWeight,
                        fontSize: menuTextStyles.fontSize,
                        '&:hover': {
                          color: '#5c0080',
                        },
                      }}
                    >
                      {page.title} <ArrowDropDownIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
                    </Button>
                    <Menu
                      anchorEl={aboutAnchorEl}
                      open={Boolean(aboutAnchorEl)}
                      onClose={handleAboutMenuClose}
                      MenuListProps={{
                        'aria-labelledby': 'about-button',
                      }}
                    >
                      {aboutSubmenu.map((item) => (
                        <MenuItem key={item.title} component={Link} to={item.path} onClick={handleAboutMenuClose}>
                          <Typography sx={menuTextStyles}>{item.title}</Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                );
              }

              if (page.title === 'Products') {
                return (
                  <Box key={page.title}>
                    <Button
                      onClick={handleProductsMenuOpen}
                      sx={{
                        my: 2,
                        color: '#3c005a',
                        display: 'block',
                        fontWeight: menuTextStyles.fontWeight,
                        fontSize: menuTextStyles.fontSize,
                        '&:hover': {
                          color: '#5c0080',
                        },
                      }}
                    >
                      {page.title} <ArrowDropDownIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
                    </Button>
                    <Menu
                      anchorEl={productsAnchorEl}
                      open={Boolean(productsAnchorEl)}
                      onClose={handleProductsMenuClose}
                      MenuListProps={{
                        'aria-labelledby': 'products-button',
                      }}
                    >
                      {productsSubmenu.map((item) => (
                        <MenuItem key={item.title} component={Link} to={item.path} onClick={handleProductsMenuClose}>
                          <Typography sx={menuTextStyles}>{item.title}</Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                );
              }

              if (page.title === 'Services') {
                return (
                  <Box key={page.title}>
                    <Button
                      onClick={handleServicesMenuOpen}
                      sx={{
                        my: 2,
                        color: '#3c005a',
                        display: 'block',
                        fontWeight: menuTextStyles.fontWeight,
                        fontSize: menuTextStyles.fontSize,
                        '&:hover': {
                          color: '#5c0080',
                        },
                      }}
                    >
                      {page.title} <ArrowDropDownIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
                    </Button>
                    <Menu
                      anchorEl={servicesAnchorEl}
                      open={Boolean(servicesAnchorEl)}
                      onClose={handleServicesMenuClose}
                      MenuListProps={{
                        'aria-labelledby': 'services-button',
                      }}
                    >
                      {/* <MenuItem component={Link} to="/services" onClick={handleServicesMenuClose}>
                        <Typography sx={menuTextStyles}>All Services</Typography>
                      </MenuItem> */}
                      {servicesSubmenu.map((item) => (
                        <MenuItem key={item.title} component={Link} to={item.path} onClick={handleServicesMenuClose}>
                          <Typography sx={menuTextStyles}>{item.title}</Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                );
              }

              return (
                <Button
                  key={page.title}
                  component={Link}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  sx={{
                    my: 2,
                    color: '#3c005a',
                    display: 'block',
                    fontWeight: menuTextStyles.fontWeight,
                    fontSize: menuTextStyles.fontSize,
                    '&:hover': {
                      color: '#5c0080',
                    },
                  }}
                >
                  {page.title}
                </Button>
              );
            })}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;